import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Spin, Empty, Select, DatePicker, Pagination, Button, Modal, Drawer } from "antd";
import { DollarOutlined, WalletOutlined, RiseOutlined, FallOutlined, CloseOutlined } from "@ant-design/icons";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { previewEstadisticasReportPdf, printEstadisticasReportPdf } from "../../components/utils/printEstadisticasReportPdf";
import { toast } from "../../components/toasts/toastStore";
import { apiGeneralInstance, authHeaderGeneral } from "../../redux/actions/general/general";
import "./EstadisticasPage.css";

dayjs.locale("es");
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const { RangePicker } = DatePicker;

const DONUT_COLORS = ["#f90336", "#3d0011", "#f59e0b", "#16a34a", "#8b5cf6", "#0ea5e9", "#64748b", "#ec4899"];

const PERIODO_OPTIONS = [
  { value: "semana", label: "Esta semana" },
  { value: "quincena", label: "Esta quincena" },
  { value: "mes", label: "Este mes" },
  { value: "custom", label: "Personalizado" },
];

const PAGE_SIZE = 10;

const TIPO_KPIS = [
  { tipo: "producto", label: "PRODUCTOS", icon: "fa-solid fa-box", valueColor: "#b31b25", iconBg: "#fff1f3" },
  { tipo: "servicio", label: "SERVICIOS", icon: "fa-solid fa-screwdriver-wrench", valueColor: "#92400e", iconBg: "#fef9c3" },
];

const KPI_DRAWER_TITLES = {
  ventas: "Ventas totales",
  gastos: "Gastos del período",
  utilidad: "Utilidad neta",
  tickets: "Tickets del período",
  ...TIPO_KPIS.reduce((acc, k) => {
    acc[k.tipo] = k.label.charAt(0) + k.label.slice(1).toLowerCase();
    return acc;
  }, {}),
};

const fmtMoney = (val) => {
  const n = parseFloat(String(val ?? 0));
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
};

const fmtMoney2 = (val) => {
  const n = parseFloat(String(val ?? 0));
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

const fmtFechaCorta = (v) => (v && dayjs(v).isValid() ? dayjs(v).format("D MMM YYYY") : "—");
const fmtHora = (v) => (v && dayjs(v).isValid() ? dayjs(v).format("HH:mm") : "—");

const ventaTipoLabel = (v) =>
  [v.total_producto > 0 && "Productos", v.total_servicio > 0 && "Servicios"].filter(Boolean).join(" + ") || "—";

function periodLabel(tipo, dateFrom, dateTo) {
  if (!dateFrom) return "";
  const df = dayjs(dateFrom);
  const dt = dayjs(dateTo);
  if (tipo === "semana") return `Semana del ${df.format("D")} al ${dt.format("D [de] MMMM YYYY")}`;
  if (tipo === "quincena") return `Quincena del ${df.format("D")} al ${dt.format("D [de] MMMM YYYY")}`;
  if (tipo === "mes") return df.format("MMMM YYYY").replace(/^\w/, (c) => c.toUpperCase());
  return `${df.format("D MMM YYYY")} — ${dt.format("D MMM YYYY")}`;
}

function makeHbarOptions(tooltipSuffix, money = false) {
  return {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => (money ? `  ${fmtMoney(ctx.raw)}` : `  ${ctx.raw} ${tooltipSuffix}`) } },
    },
    scales: {
      x: {
        grid: { color: "#f1f5f9" },
        ticks: money
          ? { font: { size: 11 }, callback: (v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`) }
          : { font: { size: 11 }, stepSize: 1 },
      },
      y: { grid: { display: false }, ticks: { font: { size: 11 } } },
    },
  };
}

const hbarUnidadesOpts = makeHbarOptions("vendidos");
const hbarMoneyOpts = makeHbarOptions("", true);
const hbarComprasOpts = makeHbarOptions("compras");
const hbarExistenciaOpts = makeHbarOptions("en existencia");

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${fmtMoney(ctx.raw)}` } } },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
    y: {
      grid: { color: "#f1f5f9" },
      ticks: { font: { size: 11 }, callback: (v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`) },
    },
  },
};

const donutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: { position: "right", labels: { font: { size: 12 }, padding: 12, usePointStyle: true } },
    tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${fmtMoney(ctx.raw)}` } },
  },
};

const ANALISIS_TABS = [
  { key: "productos", label: "Productos", icon: "fa-solid fa-box" },
  { key: "servicios", label: "Servicios", icon: "fa-solid fa-print" },
  { key: "proveedores", label: "Proveedores", icon: "fa-solid fa-truck-field" },
  { key: "clientes", label: "Clientes", icon: "fa-solid fa-address-book" },
  { key: "compras", label: "Compras", icon: "fa-solid fa-cart-arrow-down" },
  { key: "gastos", label: "Gastos", icon: "fa-solid fa-money-bill-wave" },
  { key: "promociones", label: "Promociones", icon: "fa-solid fa-tag" },
  { key: "reparaciones", label: "Reparaciones", icon: "fa-solid fa-screwdriver-wrench" },
  { key: "encargos", label: "Encargos/Apartados", icon: "fa-solid fa-box-archive" },
  { key: "facturacion", label: "Facturación", icon: "fa-solid fa-file-invoice-dollar" },
  { key: "devoluciones", label: "Devoluciones", icon: "fa-solid fa-rotate-left" },
  { key: "inventario", label: "Inventario", icon: "fa-solid fa-boxes-stacked" },
  { key: "usuarios", label: "Usuarios", icon: "fa-solid fa-user" },
];

const hbarVentasOpts = makeHbarOptions("ventas");
const hbarCantidadOpts = makeHbarOptions("registros");

// Tabs de análisis con dos gráficas de barras horizontales: [título, color, filas, campoEtiqueta, campoValor, opciones, vacío]
const ANALISIS_CHARTS = (an) => ({
  servicios: [
    ["Servicios más vendidos (piezas)", "#6366f1", an.servicios_top, "nombre", "cantidad", hbarUnidadesOpts, "Sin servicios vendidos en el período"],
    ["Servicios más vendidos (importe)", "#10b981", an.servicios_top, "nombre", "total", hbarMoneyOpts, "Sin servicios vendidos en el período"],
  ],
  proveedores: [
    ["Saldo por pagar por proveedor", "#6366f1", an.proveedores_por_pagar, "nombre", "total", hbarMoneyOpts, "Sin saldos pendientes con proveedores"],
    ["Compras por estatus de pago", "#10b981", an.compras_estatus, "nombre", "compras", hbarComprasOpts, "Sin compras en el período"],
  ],
  clientes: [
    ["Mejores clientes (importe)", "#6366f1", an.clientes_top, "nombre", "total", hbarMoneyOpts, "Sin ventas a clientes registrados en el período"],
    ["Mejores clientes (ventas)", "#10b981", an.clientes_top, "nombre", "ventas", hbarVentasOpts, "Sin ventas a clientes registrados en el período"],
  ],
  promociones: [
    ["Promociones más usadas (ventas)", "#6366f1", an.promociones_uso, "nombre", "ventas", hbarVentasOpts, "Sin ventas con promoción en el período"],
    ["Ventas con promoción (importe)", "#10b981", an.promociones_uso, "nombre", "total", hbarMoneyOpts, "Sin ventas con promoción en el período"],
  ],
  reparaciones: [
    ["Reparaciones por estado", "#6366f1", an.reparaciones_estado, "nombre", "cantidad", hbarCantidadOpts, "Sin reparaciones en el período"],
    ["Reparaciones por tipo de aparato", "#10b981", an.reparaciones_aparato, "nombre", "cantidad", hbarCantidadOpts, "Sin reparaciones en el período"],
  ],
  encargos: [
    ["Encargos y apartados por estado", "#6366f1", an.encargos_estado, "nombre", "cantidad", hbarCantidadOpts, "Sin encargos ni apartados en el período"],
    ["Anticipos por tipo", "#10b981", an.encargos_tipo, "nombre", "anticipo", hbarMoneyOpts, "Sin encargos ni apartados en el período"],
  ],
  facturacion: [
    ["Ventas por estado de facturación (importe)", "#6366f1", an.facturacion_estado, "nombre", "total", hbarMoneyOpts, "Sin ventas en el período"],
    ["Ventas por estado de facturación (cantidad)", "#10b981", an.facturacion_estado, "nombre", "ventas", hbarVentasOpts, "Sin ventas en el período"],
  ],
  devoluciones: [
    ["Devoluciones por resolución", "#6366f1", an.devoluciones_resolucion, "nombre", "cantidad", hbarCantidadOpts, "Sin devoluciones en el período"],
    ["Devoluciones por destino", "#10b981", an.devoluciones_destino, "nombre", "cantidad", hbarCantidadOpts, "Sin devoluciones en el período"],
  ],
  usuarios: [
    ["Ventas por usuario (importe)", "#6366f1", an.usuarios_ventas, "nombre", "total", hbarMoneyOpts, "Sin ventas registradas por usuario en el período"],
    ["Ventas por usuario (cantidad)", "#10b981", an.usuarios_ventas, "nombre", "ventas", hbarVentasOpts, "Sin ventas registradas por usuario en el período"],
  ],
});

const hbarData = (rows, labelKey, valueKey, label, color) =>
  rows?.length
    ? {
        labels: rows.map((r) => r[labelKey]),
        datasets: [{ label, data: rows.map((r) => r[valueKey]), backgroundColor: color, borderRadius: 4, borderSkipped: false }],
      }
    : null;

export default function EstadisticasPage() {
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState("mes");
  const [customRange, setCustomRange] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [analisisTab, setAnalisisTab] = useState("productos");
  const [exportOpen, setExportOpen] = useState(false);
  const [exportHtml, setExportHtml] = useState("");
  // Drawer de detalle al hacer click en las cards del resumen del período
  const [kpiDrawer, setKpiDrawer] = useState(null); // "ventas" | "gastos" | "utilidad" | "tickets" | tipo | null

  const fetchStats = useCallback((p, range) => {
    if (p === "custom" && !range) return;
    setLoading(true);
    const params = { periodo: p };
    if (p === "custom") {
      params.desde = range[0].format("YYYY-MM-DD");
      params.hasta = range[1].format("YYYY-MM-DD");
    }
    apiGeneralInstance.get("/estadisticas", { headers: authHeaderGeneral(), params })
      .then((r) => { setData(r.data); setPage(1); })
      .catch(() => { setData(null); toast.error("No se pudieron cargar las estadisticas"); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchStats(periodo, customRange); }, [fetchStats, periodo, customRange]);

  const handlePeriodo = (val) => {
    setPeriodo(val);
    if (val !== "custom") setCustomRange(null);
  };

  const handleRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) setCustomRange(dates);
  };

  const ventasPaginadas = useMemo(() => {
    if (!data?.ventas_del_periodo) return [];
    const start = (page - 1) * PAGE_SIZE;
    return data.ventas_del_periodo.slice(start, start + PAGE_SIZE);
  }, [data, page]);

  const totalVentasPeriodo = useMemo(
    () => (data?.ventas_del_periodo ?? []).reduce((s, v) => s + (v.total || 0), 0), [data]);
  const totalGastosPeriodo = useMemo(
    () => (data?.gastos_del_periodo ?? []).reduce((s, g) => s + (g.monto || 0), 0), [data]);

  const k = data?.kpis;
  const utilidadPositiva = (k?.utilidad_neta ?? 0) >= 0;
  const periodoDescripcion = data ? periodLabel(data.periodo.tipo, data.periodo.date_from, data.periodo.date_to) : "";

  const ventasDiaData = data?.serie?.length ? {
    labels: data.serie.map((d) => dayjs(d.fecha).format("D MMM")),
    datasets: [{ label: "Ventas", data: data.serie.map((d) => d.ventas), backgroundColor: "#f90336", borderRadius: 4, borderSkipped: false }],
  } : null;

  const pagoData = data?.ventas?.por_pago?.length ? {
    labels: data.ventas.por_pago.map((p) => p.nombre),
    datasets: [{
      data: data.ventas.por_pago.map((p) => p.total),
      backgroundColor: data.ventas.por_pago.map((_, i) => DONUT_COLORS[i % DONUT_COLORS.length]),
      borderWidth: 0,
      hoverOffset: 6,
    }],
  } : null;

  const topItemsCant = hbarData(data?.ventas?.top_items, "nombre", "cantidad", "Vendidos", "#6366f1");
  const topItemsTotal = hbarData(data?.ventas?.top_items, "nombre", "total", "Ventas", "#10b981");
  const comprasTotal = hbarData(data?.compras?.por_proveedor, "nombre", "total", "Compras", "#6366f1");
  const comprasCant = hbarData(data?.compras?.por_proveedor, "nombre", "compras", "Compras", "#10b981");
  const gastosData = hbarData(data?.gastos?.por_concepto, "nombre", "total", "Gastos", "#6366f1");
  const stockBajoData = hbarData(
    data?.inventario?.stock_bajo?.map((s) => ({ ...s, etiqueta: `${s.nombre} (${s.clave})` })),
    "etiqueta", "existencia", "Existencia", "#f90336",
  );

  const handleExport = async () => {
    if (!data) return;
    const html = await previewEstadisticasReportPdf({ data, periodoDescripcion });
    setExportHtml(html);
    setExportOpen(true);
  };

  const chartBlock = (title, dot, chartData, opts, emptyText) => (
    <div className="est-chart-inner">
      <p className="est-chart-title"><span className="est-chart-dot" style={{ background: dot }} />{title}</p>
      <div className="est-hbar-wrap">
        {chartData ? <Bar data={chartData} options={opts} /> : <Empty description={emptyText} style={{ margin: "24px 0" }} />}
      </div>
    </div>
  );

  return (
    <div className="est-main">
      <div className="est-content">

        {/* ── TITLE ── */}
        <div className="est-page-header-card">
          <h1 className="est-page-title">Estadísticas</h1>
          <Button onClick={handleExport} disabled={!data || loading} style={{ flex: "none" }} className="est-export-btn">
            Exportar
          </Button>
        </div>

        {/* ── RESUMEN ── */}
        <div className="est-section-card">
          <div className="est-section-header">
            <div className="est-header-left">
              <span className="est-section-label">RESUMEN DEL PERIODO</span>
              {periodoDescripcion && <span className="est-period-display">{periodoDescripcion}</span>}
            </div>
            <div className="est-period-selector">
              <Select value={periodo} onChange={handlePeriodo} options={PERIODO_OPTIONS} style={{ width: 160 }} size="middle" className="est-select" />
              {periodo === "custom" && (
                <RangePicker onChange={handleRangeChange} format="DD/MM/YYYY" placeholder={["Desde", "Hasta"]} style={{ marginLeft: 8 }} />
              )}
            </div>
          </div>

          {loading ? (
            <div className="est-loading-wrap"><Spin size="large" /></div>
          ) : !data ? (
            <Empty description="No se pudieron cargar las estadísticas" style={{ margin: "40px 0" }} />
          ) : (
            <>
              <div className="est-kpi-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                <div className="est-kpi-card est-kpi-card-clickable" onClick={() => setKpiDrawer("ventas")}>
                  <span className="est-kpi-icon-wrap est-kpi-icon-green"><DollarOutlined /></span>
                  <span className="est-kpi-label">VENTAS TOTALES</span>
                  <p className="est-kpi-value est-kpi-green">{fmtMoney2(k.ventas)}</p>
                  <span className="est-kpi-sub">{k.tickets} ticket{k.tickets !== 1 ? "s" : ""} en el período</span>
                </div>
                <div className="est-kpi-card est-kpi-card-clickable est-kpi-card-rose" onClick={() => setKpiDrawer("gastos")}>
                  <span className="est-kpi-icon-wrap est-kpi-icon-rose"><i className="fa-solid fa-file-lines" /></span>
                  <span className="est-kpi-label">GASTOS</span>
                  <p className="est-kpi-value" style={{ color: "#9f1239" }}>{fmtMoney2(k.gastos)}</p>
                  <span className="est-kpi-sub">total registrado en el período</span>
                </div>
                <div className={`est-kpi-card est-kpi-card-clickable ${utilidadPositiva ? "" : "est-kpi-card-red"}`} onClick={() => setKpiDrawer("utilidad")}>
                  <span className={`est-kpi-icon-wrap ${utilidadPositiva ? "est-kpi-icon-green" : "est-kpi-icon-red"}`}>
                    {utilidadPositiva ? <RiseOutlined /> : <FallOutlined />}
                  </span>
                  <span className="est-kpi-label">UTILIDAD NETA</span>
                  <p className={`est-kpi-value ${utilidadPositiva ? "est-kpi-green" : "est-kpi-red"}`}>{fmtMoney2(k.utilidad_neta)}</p>
                  <span className="est-kpi-sub">ventas menos gastos del período</span>
                </div>
              </div>

              <p className="est-list-heading" style={{ marginTop: 20 }}>Ventas por tipo</p>
              <div className="est-kpi-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                <div className="est-kpi-card est-kpi-card-clickable" onClick={() => setKpiDrawer("tickets")}>
                  <span className="est-kpi-icon-wrap est-kpi-icon-indigo"><WalletOutlined /></span>
                  <span className="est-kpi-label">TICKETS</span>
                  <p className="est-kpi-value est-kpi-indigo">{k.tickets}</p>
                  <span className="est-kpi-sub">ticket{k.tickets !== 1 ? "s" : ""} en el período</span>
                </div>
                {TIPO_KPIS.map(({ tipo, label, icon, valueColor, iconBg }) => {
                  const row = data.ventas.por_tipo.find((t) => t.nombre === tipo);
                  return (
                    <div className="est-kpi-card est-kpi-card-clickable" key={tipo} onClick={() => setKpiDrawer(tipo)}>
                      <span className="est-kpi-icon-wrap" style={{ background: iconBg, color: valueColor }}><i className={icon} /></span>
                      <span className="est-kpi-label">{label}</span>
                      <p className="est-kpi-value" style={{ color: valueColor }}>{fmtMoney2(row?.total)}</p>
                      <span className="est-kpi-sub">{row?.cantidad ?? 0} venta{row?.cantidad === 1 ? "" : "s"}</span>
                    </div>
                  );
                })}
              </div>

              <div className="est-charts-wrap">
                <div className="est-chart-main">
                  <p className="est-chart-title">Ventas por día</p>
                  <div className="est-bar-wrap">
                    {ventasDiaData ? <Bar data={ventasDiaData} options={barOptions} /> : <Empty description="Sin ventas en el período" style={{ margin: "32px 0" }} />}
                  </div>
                </div>
                <div className="est-chart-side">
                  <p className="est-chart-title">Ventas por forma de pago</p>
                  <div className="est-donut-wrap">
                    {pagoData ? <Doughnut data={pagoData} options={donutOptions} /> : <Empty description="Sin pagos en el período" style={{ margin: "32px 0" }} />}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── CORTES DE CAJA DEL PERÍODO ── */}
        {!loading && data && (
          <div className="est-section-card">
            <div className="est-section-header est-section-header-sm">
              <div className="est-header-left">
                <span className="est-section-label">CORTES DE CAJA DEL PERÍODO</span>
                {periodoDescripcion && <span className="est-period-display">{periodoDescripcion}</span>}
              </div>
              <Button className="est-export-btn" icon={<i className="fa-solid fa-arrow-right" />} iconPosition="end" onClick={() => navigate("/app/cortes")}>
                Ir a Cortes
              </Button>
            </div>

            {data.cortes.length > 0 ? (
              <div className="est-cortes-scroll">
                <div className="est-cortes-head">
                  <span>Corte</span>
                  <span>Fecha</span>
                  <span style={{ textAlign: "right" }}>Esperado</span>
                  <span style={{ textAlign: "right" }}>Efectivo</span>
                  <span style={{ textAlign: "right" }}>Tarjeta</span>
                  <span style={{ textAlign: "right" }}>Transf.</span>
                  <span style={{ textAlign: "right" }}>Contado</span>
                  <span style={{ textAlign: "right" }}>Diferencia</span>
                  <span>Cerró</span>
                </div>
                <div className="est-cortes-list">
                  {data.cortes.map((c) => {
                    const diffClass = c.diferencia > 0 ? "est-corte-diff-pos" : c.diferencia < 0 ? "est-corte-diff-neg" : "est-corte-diff-zero";
                    return (
                      <div key={c.id_corte} className="est-corte-row">
                        <span className="est-corte-folio">#{c.id_corte}</span>
                        <span className="est-contrato-fecha">{fmtFechaCorta(c.fecha)}</span>
                        <span className="est-corte-money">{fmtMoney2(c.total_esperado)}</span>
                        <span className="est-corte-money">{fmtMoney2(c.efectivo)}</span>
                        <span className="est-corte-money">{fmtMoney2(c.tarjeta)}</span>
                        <span className="est-corte-money">{fmtMoney2(c.transferencia)}</span>
                        <span className="est-corte-money">{fmtMoney2(c.total_contado)}</span>
                        <span className={`est-corte-money ${diffClass}`}>{fmtMoney2(c.diferencia)}</span>
                        <span><span className="est-corte-badge">{c.confirmo_nombre || "—"}</span></span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <Empty description="Sin cortes de caja cerrados en este período" style={{ margin: "32px 0" }} />
            )}

            <div className="est-cortes-footer">
              <span className="est-count-badge">{data.cortes.length} corte{data.cortes.length === 1 ? "" : "s"}</span>
            </div>
          </div>
        )}

        {/* ── ANÁLISIS DEL PERÍODO ── */}
        {!loading && data && (
          <div className="est-section-card">
            <div className="est-section-header est-section-header-sm">
              <div className="est-header-left">
                <span className="est-section-label">ANÁLISIS DEL PERÍODO</span>
                {periodoDescripcion && <span className="est-period-display">{periodoDescripcion}</span>}
              </div>
            </div>

            <div className="est-analysis-body">
              <div className="est-analysis-tabs">
                {ANALISIS_TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`est-analysis-tab${analisisTab === t.key ? " est-analysis-tab--active" : ""}`}
                    onClick={() => setAnalisisTab(t.key)}
                  >
                    <i className={t.icon} />
                    <span>{t.label}</span>
                  </button>
                ))}
              </div>

              <div className="est-analysis-content">
                {analisisTab === "productos" && (
                  <div className="est-analysis-row">
                    {chartBlock("Lo que más se vende (piezas)", "#6366f1", topItemsCant, hbarUnidadesOpts, "Sin ventas en el período")}
                    {chartBlock("Lo que más se vende (importe)", "#10b981", topItemsTotal, hbarMoneyOpts, "Sin ventas en el período")}
                    <div style={{ gridColumn: "1 / -1" }}>
                      {chartBlock("Ventas por categoría", "#f90336", hbarData(data.analisis.ventas_categoria, "nombre", "total", "Ventas", "#f90336"), hbarMoneyOpts, "Sin ventas de productos en el período")}
                    </div>
                  </div>
                )}

                {ANALISIS_CHARTS(data.analisis)[analisisTab] && (
                  <div className="est-analysis-row">
                    {ANALISIS_CHARTS(data.analisis)[analisisTab].map(([title, color, rows, lk, vk, opts, empty]) => (
                      <React.Fragment key={title}>
                        {chartBlock(title, color, hbarData(rows, lk, vk, title, color), opts, empty)}
                      </React.Fragment>
                    ))}
                  </div>
                )}

                {analisisTab === "compras" && (
                  <div className="est-analysis-row">
                    {chartBlock("Compras por proveedor (importe)", "#6366f1", comprasTotal, hbarMoneyOpts, "Sin compras en el período")}
                    {chartBlock("Compras por proveedor (cantidad)", "#10b981", comprasCant, hbarComprasOpts, "Sin compras en el período")}
                  </div>
                )}

                {analisisTab === "gastos" && (
                  <div className="est-analysis-row">
                    <div className="est-chart-inner" style={{ gridColumn: "1 / -1" }}>
                      <p className="est-chart-title"><span className="est-chart-dot" style={{ background: "#6366f1" }} />Gastos por concepto</p>
                      <div className="est-hbar-wrap">
                        {gastosData ? <Bar data={gastosData} options={hbarMoneyOpts} /> : <Empty description="Sin gastos en el período" style={{ margin: "24px 0" }} />}
                      </div>
                    </div>
                  </div>
                )}

                {analisisTab === "inventario" && (
                  <>
                    <div className="est-kpi-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", marginBottom: 20 }}>
                      <div className="est-kpi-card">
                        <span className="est-kpi-label">VALOR A COSTO</span>
                        <p className="est-kpi-value est-kpi-indigo">{fmtMoney2(data.inventario.valor_costo)}</p>
                        <span className="est-kpi-sub">foto actual del inventario</span>
                      </div>
                      <div className="est-kpi-card">
                        <span className="est-kpi-label">VALOR A PRECIO DE VENTA</span>
                        <p className="est-kpi-value est-kpi-green">{fmtMoney2(data.inventario.valor_venta)}</p>
                        <span className="est-kpi-sub">foto actual del inventario</span>
                      </div>
                      <div className="est-kpi-card">
                        <span className="est-kpi-label">STOCK BAJO</span>
                        <p className={`est-kpi-value ${data.inventario.bajos ? "est-kpi-red" : "est-kpi-green"}`}>{data.inventario.bajos}</p>
                        <span className="est-kpi-sub">variantes en o bajo el mínimo</span>
                      </div>
                    </div>
                    <div className="est-analysis-row">
                      {chartBlock("Variantes con stock bajo (existencia)", "#f90336", stockBajoData, hbarExistenciaOpts, "Todo el inventario está por encima del mínimo")}
                      {chartBlock("Ajustes de existencia por tipo", "#6366f1", hbarData(data.analisis.ajustes_tipo, "nombre", "cantidad", "Ajustes", "#6366f1"), hbarCantidadOpts, "Sin ajustes de existencia en el período")}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal
        open={exportOpen}
        onCancel={() => setExportOpen(false)}
        title="Previsualización — Reporte de Estadísticas"
        width={1020}
        centered
        destroyOnClose
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setExportOpen(false)} style={{ background: "#374151", borderColor: "#374151", color: "#fff" }}>
              Cerrar
            </Button>
            <Button className="est-export-btn" onClick={() => printEstadisticasReportPdf({ data, periodoDescripcion })}>
              Exportar PDF
            </Button>
          </div>
        }
      >
        <div style={{ height: "72vh", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
          <iframe title="preview-estadisticas" style={{ width: "100%", height: "100%", border: 0, background: "#fff" }} srcDoc={exportHtml} />
        </div>
      </Modal>

      <Drawer
        open={!!kpiDrawer}
        onClose={() => setKpiDrawer(null)}
        placement="right"
        width={480}
        destroyOnClose
        mask={false}
        closable={false}
        className="est-kpi-drawer"
      >
        <div className="est-drawer-custom-header">
          <span>{KPI_DRAWER_TITLES[kpiDrawer] || ""}</span>
          <button type="button" className="est-drawer-close-btn" onClick={() => setKpiDrawer(null)} aria-label="Cerrar">
            <CloseOutlined />
          </button>
        </div>

        {data && (kpiDrawer === "ventas" || kpiDrawer === "tickets") && (
          <div className="est-drawer-body">
            <div className="est-section-header est-section-header-sm" style={{ padding: 0, marginBottom: 12 }}>
              <div className="est-header-left">
                <span className="est-section-label">VENTAS DEL PERÍODO</span>
                <span className="est-period-display">{periodoDescripcion}</span>
              </div>
              <span className="est-count-badge">{data.ventas_del_periodo.length} ventas</span>
            </div>

            <button type="button" className="est-drawer-link" onClick={() => { setKpiDrawer(null); navigate("/app/ventas"); }}>
              Ver historial completo de ventas
              <i className="fa-solid fa-arrow-right" />
            </button>

            {data.ventas_del_periodo.length > 0 ? (
              <>
                <div className="est-contratos-head">
                  <span>Folio</span>
                  <span>Tipo</span>
                  <span>Fecha</span>
                  <span>Hora</span>
                  <span style={{ textAlign: "right" }}>Total</span>
                </div>
                <div className="est-contratos-list">
                  {ventasPaginadas.map((v) => (
                    <div key={v.id_venta} className="est-contrato-row">
                      <div className="est-contrato-nombre">
                        <span className="est-dot est-dot-blue" style={{ flexShrink: 0 }} />
                        <span>#{v.id_venta}</span>
                      </div>
                      <span className="est-contrato-tipo">{ventaTipoLabel(v)}</span>
                      <span className="est-contrato-fecha">{fmtFechaCorta(v.fecha)}</span>
                      <span className="est-contrato-fecha">{fmtHora(v.fecha)}</span>
                      <span className="est-contrato-importe">{fmtMoney2(v.total)}</span>
                    </div>
                  ))}
                </div>
                <div className="est-contratos-total-row">
                  <span className="est-total-label">Total ({data.ventas_del_periodo.length} ventas)</span>
                  <span /><span /><span />
                  <span className="est-total-importe">{fmtMoney2(totalVentasPeriodo)}</span>
                </div>
                {data.ventas_del_periodo.length > PAGE_SIZE && (
                  <div className="est-pagination">
                    <Pagination current={page} pageSize={PAGE_SIZE} total={data.ventas_del_periodo.length} onChange={setPage} size="small" showSizeChanger={false} />
                  </div>
                )}
              </>
            ) : (
              <Empty description="Sin ventas en este período" style={{ margin: "32px 0" }} />
            )}
          </div>
        )}

        {data && TIPO_KPIS.some((t) => t.tipo === kpiDrawer) && (() => {
          const campo = kpiDrawer === "producto" ? "total_producto" : "total_servicio";
          const ventasTipo = data.ventas_del_periodo.filter((v) => v[campo] > 0);
          const totalTipo = ventasTipo.reduce((s, v) => s + (v[campo] || 0), 0);
          return (
            <div className="est-drawer-body">
              <div className="est-section-header est-section-header-sm" style={{ padding: 0, marginBottom: 12 }}>
                <div className="est-header-left">
                  <span className="est-section-label">VENTAS · {KPI_DRAWER_TITLES[kpiDrawer]?.toUpperCase()}</span>
                  <span className="est-period-display">{periodoDescripcion}</span>
                </div>
                <span className="est-count-badge">{ventasTipo.length} ventas</span>
              </div>

              <button type="button" className="est-drawer-link" onClick={() => { setKpiDrawer(null); navigate("/app/ventas"); }}>
                Ver historial completo de ventas
                <i className="fa-solid fa-arrow-right" />
              </button>

              {ventasTipo.length > 0 ? (
                <>
                  <div className="est-contratos-head" style={{ gridTemplateColumns: "1.6fr 1fr 1fr 1fr" }}>
                    <span>Folio</span>
                    <span>Fecha</span>
                    <span>Hora</span>
                    <span style={{ textAlign: "right" }}>Total</span>
                  </div>
                  <div className="est-contratos-list">
                    {ventasTipo.map((v) => (
                      <div key={v.id_venta} className="est-contrato-row" style={{ gridTemplateColumns: "1.6fr 1fr 1fr 1fr" }}>
                        <div className="est-contrato-nombre">
                          <span className="est-dot est-dot-blue" style={{ flexShrink: 0 }} />
                          <span>#{v.id_venta}</span>
                        </div>
                        <span className="est-contrato-fecha">{fmtFechaCorta(v.fecha)}</span>
                        <span className="est-contrato-fecha">{fmtHora(v.fecha)}</span>
                        <span className="est-contrato-importe">{fmtMoney2(v[campo])}</span>
                      </div>
                    ))}
                  </div>
                  <div className="est-contratos-total-row" style={{ gridTemplateColumns: "1.6fr 1fr 1fr 1fr" }}>
                    <span className="est-total-label">Total ({ventasTipo.length} ventas)</span>
                    <span /><span />
                    <span className="est-total-importe">{fmtMoney2(totalTipo)}</span>
                  </div>
                </>
              ) : (
                <Empty description="Sin ventas de este tipo en el período" style={{ margin: "32px 0" }} />
              )}
            </div>
          );
        })()}

        {data && kpiDrawer === "gastos" && (
          <div className="est-drawer-body">
            <div className="est-section-header est-section-header-sm" style={{ padding: 0, marginBottom: 12 }}>
              <div className="est-header-left">
                <span className="est-section-label">GASTOS DEL PERÍODO</span>
                <span className="est-period-display">{periodoDescripcion}</span>
              </div>
            </div>

            <button type="button" className="est-drawer-link" onClick={() => { setKpiDrawer(null); navigate("/app/gastos"); }}>
              Ver todos los gastos
              <i className="fa-solid fa-arrow-right" />
            </button>

            {data.gastos_del_periodo.length > 0 ? (
              <>
                <p className="est-list-heading">Gastos registrados en el período</p>
                <div className="est-vencidos-list">
                  {data.gastos_del_periodo.map((g) => (
                    <div key={g.id_gasto} className="est-vencido-row" onClick={() => { setKpiDrawer(null); navigate(`/app/gastos/${g.id_gasto}`); }}>
                      <div className="est-vencido-left">
                        <span className="est-dot est-dot-blue" />
                        <div>
                          <p className="est-vencido-nombre">{g.descripcion}</p>
                          <p className="est-vencido-sub">{fmtFechaCorta(g.fecha)}{g.nota ? ` · ${g.nota}` : ""}</p>
                        </div>
                      </div>
                      <div className="est-vencido-right">
                        <span className="est-vencido-monto">{fmtMoney2(g.monto)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="est-cobranza-total-row">
                  <span className="est-total-label">Total gastos ({data.gastos_del_periodo.length})</span>
                  <span className="est-cobranza-total-monto">{fmtMoney2(totalGastosPeriodo)}</span>
                </div>
              </>
            ) : (
              <Empty description="Sin gastos registrados en este período" style={{ margin: "32px 0" }} />
            )}
          </div>
        )}

        {data && kpiDrawer === "utilidad" && (
          <div className="est-drawer-body">
            <div className="est-drawer-hero">
              <span className="est-drawer-hero-label">UTILIDAD NETA</span>
              <span className={`est-drawer-hero-value ${utilidadPositiva ? "est-kpi-green" : "est-kpi-red"}`}>{fmtMoney2(data.kpis.utilidad_neta)}</span>
              <span className="est-drawer-hero-sub">ventas menos gastos · {periodoDescripcion}</span>
            </div>

            <p className="est-list-heading" style={{ marginTop: 20 }}>Cómo se calculó</p>
            <div className="est-drawer-row">
              <span className="est-kpi-icon-wrap est-kpi-icon-green" style={{ margin: 0 }}><DollarOutlined /></span>
              <div className="est-drawer-row-info">
                <span className="est-drawer-row-label">Ventas totales</span>
                <span className="est-drawer-row-sub">{data.kpis.tickets} tickets</span>
              </div>
              <span className="est-drawer-row-value est-kpi-green">+{fmtMoney2(data.kpis.ventas)}</span>
            </div>
            <div className="est-drawer-row">
              <span className="est-kpi-icon-wrap est-kpi-icon-rose" style={{ margin: 0 }}><i className="fa-solid fa-file-lines" /></span>
              <div className="est-drawer-row-info">
                <span className="est-drawer-row-label">Gastos</span>
                <span className="est-drawer-row-sub">{data.gastos_del_periodo.length} registrados</span>
              </div>
              <span className="est-drawer-row-value" style={{ color: "#9f1239" }}>−{fmtMoney2(data.kpis.gastos)}</span>
            </div>
            <div className="est-drawer-row est-drawer-row-total">
              <div className="est-drawer-row-info"><span className="est-drawer-row-label">Utilidad neta</span></div>
              <span className={`est-drawer-row-value ${utilidadPositiva ? "est-kpi-green" : "est-kpi-red"}`}>{fmtMoney2(data.kpis.utilidad_neta)}</span>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
